import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { Button, Field, Input, Textarea, Icon, Modal, Note } from '../ui';

/** Төрийн банктай холбогдоогүй СӨХ-д (нэвтэрсэн админд) 3 удаа харуулах алерт + «Админд мэдэгдэх» хүсэлтийн форм.
 *  Тоолуур: localStorage `bpay_alert_shows:{tenantId}` — нэг browser session-д 1 удаа тоолно.
 *  Хүсэлт илгээсний дараа алерт гарахгүй (жижиг «хүсэлт илгээсэн» тэмдэглэл л). */

const MAX_SHOWS = 3;
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1').replace(/\/api\/v1\/?$/, '');
const errText = (e: any, fb: string) => { try { return JSON.parse(e?.message).detail || fb; } catch { return e?.message || fb; } };

export default function BankConnectAlert({ tenantId, role }: { tenantId?: string | null; role?: string }) {
  const [state, setState] = useState<{ connected: boolean; request: any } | null>(null);
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [cert, setCert] = useState<File | null>(null); const [idc, setIdc] = useState<File | null>(null);
  const [note, setNote] = useState(''); const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(''); const [done, setDone] = useState('');
  const certRef = useRef<HTMLInputElement>(null); const idRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!tenantId || role === 'platform_admin' || role === 'district') return;
    api.get<any>('/admin/bpay-request').then(r => {
      setState(r);
      if (r.connected || r.request) return;              // холбогдсон эсвэл хүсэлт илгээсэн → алерт хэрэггүй
      const key = `bpay_alert_shows:${tenantId}`; const sessKey = `bpay_alert_counted:${tenantId}`;
      let shows = parseInt(localStorage.getItem(key) || '0', 10) || 0;
      if (!sessionStorage.getItem(sessKey)) { shows += 1; try { localStorage.setItem(key, String(shows)); sessionStorage.setItem(sessKey, '1'); } catch {} }
      if (shows <= MAX_SHOWS) setVisible(true);
    }).catch(() => {});
  }, [tenantId, role]);

  const submit = async () => {
    if (!cert || !idc) { setErr('СӨХ-ийн гэрчилгээ болон захирлын иргэний үнэмлэхийн хуулбар хоёуланг нь хавсаргана уу'); return; }
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('certificate', cert); fd.append('id_card', idc); fd.append('note', note); fd.append('contact_phone', phone);
      const r = await fetch(`${API_ORIGIN}/api/v1/admin/bpay-request`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}` }, body: fd });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail || 'Илгээхэд алдаа');
      setDone(d.message || 'Хүсэлт илгээгдлээ'); setState(s => s ? { ...s, request: d.request } : s); setVisible(false);
    } catch (e: any) { setErr(errText(e, 'Илгээхэд алдаа гарлаа')); } finally { setBusy(false); }
  };

  if (!state || state.connected) return null;
  if (!visible && !open && !done) return null;

  return (
    <>
      {visible && !done && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '12px 16px', margin: '0 0 16px', borderRadius: 12, background: '#fef3c7', border: '1px solid #fcd34d', color: '#78350f' }}>
          <Icon name="alert" size={22} style={{ color: '#d97706', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 240, fontSize: 14, lineHeight: 1.45 }}>
            <b>Төрийн банкны холболт хийгдээгүй байна.</b> Та системийн админтай холбогдож, Төрийн банкны холболт хийлгэнэ үү — үүний дараа оршин суугчид QR-аар СӨХ, дулаан, цахилгааны төлбөрөө төлж, төлөлт автоматаар бүртгэгдэнэ.
          </div>
          <Button size="sm" onClick={() => setOpen(true)} style={{ background: '#d97706', border: 0 }}>Энд дарж Админд мэдэгдэх</Button>
          <button onClick={() => setVisible(false)} aria-label="Хаах" style={{ background: 'transparent', border: 0, cursor: 'pointer', color: '#92400e', display: 'flex' }}><Icon name="x" size={18} /></button>
        </div>
      )}
      {done && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', margin: '0 0 16px', borderRadius: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', fontSize: 13.5 }}>
          <Icon name="check-circle" size={18} />{done}
          <button onClick={() => setDone('')} aria-label="Хаах" style={{ marginLeft: 'auto', background: 'transparent', border: 0, cursor: 'pointer', color: '#166534', display: 'flex' }}><Icon name="x" size={16} /></button>
        </div>
      )}
      {open && (
        <Modal title="Төрийн банкны холболт хийлгэх хүсэлт" width={440} onClose={() => setOpen(false)}
          footer={<><Button variant="ghost" onClick={() => setOpen(false)}>Болих</Button><Button loading={busy} onClick={submit} icon="upload">Илгээх</Button></>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Note>Системийн админ доорх баримтуудыг үндэслэн Төрийн банкинд танай СӨХ-ийн хаяг, данс, QPay-г холбоно. Баримтууд зөвхөн платформ админд харагдана.</Note>
            {err && <Note tone="danger" icon="alert">{err}</Note>}
            <Field label="СӨХ-ийн гэрчилгээ (зураг / PDF)" id="bc-cert">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button variant="secondary" size="sm" onClick={() => certRef.current?.click()}>{cert ? 'Солих' : 'Файл сонгох'}</Button>
                <span style={{ fontSize: 13, color: cert ? 'var(--ink)' : 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cert ? cert.name : 'Сонгоогүй'}</span>
                <input ref={certRef} id="bc-cert" type="file" accept="image/*,application/pdf" hidden onChange={e => setCert(e.target.files?.[0] || null)} />
              </div>
            </Field>
            <Field label="Захирлын иргэний үнэмлэхийн хуулбар (зураг / PDF)" id="bc-id">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button variant="secondary" size="sm" onClick={() => idRef.current?.click()}>{idc ? 'Солих' : 'Файл сонгох'}</Button>
                <span style={{ fontSize: 13, color: idc ? 'var(--ink)' : 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{idc ? idc.name : 'Сонгоогүй'}</span>
                <input ref={idRef} id="bc-id" type="file" accept="image/*,application/pdf" hidden onChange={e => setIdc(e.target.files?.[0] || null)} />
              </div>
            </Field>
            <Field label="Холбогдох утас" id="bc-phone" optional><Input id="bc-phone" inputMode="numeric" maxLength={8} value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="99112233" /></Field>
            <Field label="Нэмэлт тайлбар" id="bc-note" optional><Textarea id="bc-note" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Байрны хаяг, Төрийн банкны дансны дугаар гэх мэт" /></Field>
          </div>
        </Modal>
      )}
    </>
  );
}
